import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { E2EAgentConflictError } from "../../api/transport";
import {
  agentQueryKey,
  applyOptimisticXLMCellEdit,
  applyXLMCellPatches,
  applyXLMMutation,
  fetchXLMWorkbookSnapshot,
  mergeXLMEditCellOperations,
  normalizeXLMRevisionDelta,
  normalizeXLMRevisionMutation,
  updateXLMRevisionList,
  xlmCellAddressKey,
  type XLMEditCellOperation,
  type XLMRevisionDelta,
  type XLMWorkbookSnapshot,
} from "../api/e2e";
import { e2eAgentJson, postE2EAgentJson } from "../../api/transport";

type QueueItemState = "queued" | "sending" | "failed" | "conflict";

interface QueueItem {
  id: string;
  idempotencyKey: string;
  operations: XLMEditCellOperation[];
  state: QueueItemState;
  baseRevision: number | null;
  error: Error | null;
  conflictRevision: number | null;
}

export type XLMEditQueueStatus =
  | "idle"
  | "saving"
  | "saved"
  | "failed"
  | "conflict";

export interface XLMEditQueueState {
  status: XLMEditQueueStatus;
  pendingCount: number;
  hasPending: boolean;
  lastSavedRevision: number | null;
  error: Error | null;
  conflictRevision: number | null;
}

export interface XLMEditQueue extends XLMEditQueueState {
  enqueue: (operation: XLMEditCellOperation) => boolean;
  enqueueBatch: (operations: XLMEditCellOperation[]) => boolean;
  retry: () => void;
  keepMine: () => void;
  useLatest: () => Promise<void>;
}

const EMPTY_STATE: XLMEditQueueState = {
  status: "idle",
  pendingCount: 0,
  hasPending: false,
  lastSavedRevision: null,
  error: null,
  conflictRevision: null,
};

function queueState(
  items: readonly QueueItem[],
  lastSavedRevision: number | null,
): XLMEditQueueState {
  const conflict = items.find((item) => item.state === "conflict");
  const failed = items.find((item) => item.state === "failed");
  const status: XLMEditQueueStatus = conflict
    ? "conflict"
    : failed
      ? "failed"
      : items.length > 0
        ? "saving"
        : lastSavedRevision === null
          ? "idle"
          : "saved";
  return {
    status,
    pendingCount: items.reduce(
      (count, item) => count + item.operations.length,
      0,
    ),
    hasPending: items.length > 0,
    lastSavedRevision,
    error: failed?.error ?? null,
    conflictRevision: conflict?.conflictRevision ?? null,
  };
}

function sameQueueState(
  left: XLMEditQueueState,
  right: XLMEditQueueState,
): boolean {
  return (
    left.status === right.status &&
    left.pendingCount === right.pendingCount &&
    left.hasPending === right.hasPending &&
    left.lastSavedRevision === right.lastSavedRevision &&
    left.error === right.error &&
    left.conflictRevision === right.conflictRevision
  );
}

function schedule(callback: () => void): void {
  queueMicrotask(callback);
}

async function postRevision(
  companyId: string,
  versionId: string,
  item: QueueItem,
) {
  if (item.baseRevision === null) {
    throw new Error("The XLM edit has no base revision.");
  }
  return normalizeXLMRevisionMutation(
    await postE2EAgentJson(
      `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/revisions`,
      {
        base_revision: item.baseRevision,
        operations: item.operations,
      },
      { "Idempotency-Key": item.idempotencyKey },
    ),
  );
}

async function fetchDelta(
  companyId: string,
  versionId: string,
  baseRevision: number,
): Promise<XLMRevisionDelta> {
  return normalizeXLMRevisionDelta(
    await e2eAgentJson(
      `/v1/companies/${encodeURIComponent(companyId)}/models/${encodeURIComponent(versionId)}/xlm/delta?base_revision=${baseRevision}`,
    ),
  );
}

function applyOptimisticOperations(
  snapshot: XLMWorkbookSnapshot,
  operations: readonly XLMEditCellOperation[],
): XLMWorkbookSnapshot | null {
  let next = snapshot;
  for (const operation of operations) {
    const edited = applyOptimisticXLMCellEdit(next, operation);
    if (!edited) return null;
    next = edited;
  }
  return next;
}

export function useXLMEditQueue(
  companyId: string,
  versionId: string,
): XLMEditQueue {
  const queryClient = useQueryClient();
  const items = useRef<QueueItem[]>([]);
  const processing = useRef(false);
  const mounted = useRef(true);
  const scope = useRef(`${companyId}:${versionId}`);
  const lastSavedRevision = useRef<number | null>(null);
  const processNext = useRef<() => void>(() => undefined);
  const [state, setState] = useState<XLMEditQueueState>(EMPTY_STATE);
  const queryKey = agentQueryKey("xlm", companyId, versionId);
  const revisionListKey = agentQueryKey("xlm-revisions", companyId, versionId);

  const publishState = useCallback(() => {
    if (!mounted.current) return;
    setState((current) => {
      const next = queueState(items.current, lastSavedRevision.current);
      return sameQueueState(current, next) ? current : next;
    });
  }, []);

  const reapplyOptimisticEdits = useCallback(() => {
    queryClient.setQueryData<XLMWorkbookSnapshot>(queryKey, (current) => {
      if (!current) return current;
      let next = current;
      for (const item of items.current) {
        const edited = applyOptimisticOperations(next, item.operations);
        if (edited) next = edited;
      }
      return next;
    });
  }, [queryClient, queryKey]);

  const reconcileConflict = useCallback(
    async (item: QueueItem, baseRevision: number): Promise<void> => {
      let delta: XLMRevisionDelta | null = null;
      let serverSnapshot: XLMWorkbookSnapshot | null = null;
      try {
        delta = await fetchDelta(companyId, versionId, baseRevision);
        const current = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
        if (
          current &&
          !delta.reload_required &&
          delta.base_revision === baseRevision &&
          current.xlm_snapshot.revision === baseRevision
        ) {
          const model = applyXLMCellPatches(current.model, delta.cell_patches);
          if (model) {
            serverSnapshot = { model, xlm_snapshot: delta.xlm_snapshot };
          }
        }
      } catch {
        // A verified full snapshot is the bounded compatibility fallback.
      }
      if (!serverSnapshot) {
        serverSnapshot = await fetchXLMWorkbookSnapshot(companyId, versionId);
      }
      queryClient.setQueryData(queryKey, serverSnapshot);
      void queryClient.invalidateQueries({ queryKey: revisionListKey });

      const changedCells = new Set(
        delta && !delta.reload_required
          ? delta.cell_patches.map((patch) => xlmCellAddressKey(patch.cell))
          : item.operations.map((operation) =>
              xlmCellAddressKey(operation.cell),
            ),
      );
      const overlapping = items.current.filter((queued) =>
        queued.operations.some((operation) =>
          changedCells.has(xlmCellAddressKey(operation.cell)),
        ),
      );
      for (const queued of overlapping) {
        queued.state = "conflict";
        queued.conflictRevision = serverSnapshot.xlm_snapshot.revision;
        queued.baseRevision = serverSnapshot.xlm_snapshot.revision;
      }
      item.baseRevision = serverSnapshot.xlm_snapshot.revision;
      if (!overlapping.includes(item)) item.state = "queued";
      reapplyOptimisticEdits();
    },
    [
      companyId,
      queryClient,
      queryKey,
      reapplyOptimisticEdits,
      revisionListKey,
      versionId,
    ],
  );

  processNext.current = () => {
    if (processing.current || companyId === "" || versionId === "") return;
    const item = items.current[0];
    if (!item || item.state === "failed" || item.state === "conflict") return;
    if (item.state === "sending") return;
    const snapshot = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
    if (!snapshot) {
      item.state = "failed";
      item.error = new Error("The model snapshot is unavailable.");
      publishState();
      return;
    }
    item.baseRevision ??= snapshot.xlm_snapshot.revision;
    item.state = "sending";
    item.error = null;
    processing.current = true;
    publishState();
    const requestScope = scope.current;
    void postRevision(companyId, versionId, item)
      .then(async (mutation) => {
        if (scope.current !== requestScope) return;
        updateXLMRevisionList(queryClient, { companyId, versionId }, mutation);
        await applyXLMMutation(queryClient, { companyId, versionId }, mutation);
        items.current = items.current.filter(
          (candidate) => candidate.id !== item.id,
        );
        lastSavedRevision.current = mutation.revision;
        reapplyOptimisticEdits();
      })
      .catch(async (error: unknown) => {
        if (scope.current !== requestScope) return;
        if (
          error instanceof E2EAgentConflictError &&
          item.baseRevision !== null
        ) {
          try {
            await reconcileConflict(item, item.baseRevision);
          } catch (reconcileError) {
            item.state = "failed";
            item.error =
              reconcileError instanceof Error
                ? reconcileError
                : new Error("The current model revision could not be loaded.");
          }
          return;
        }
        item.state = "failed";
        item.error =
          error instanceof Error ? error : new Error("The model edit failed.");
      })
      .finally(() => {
        if (scope.current !== requestScope) return;
        processing.current = false;
        publishState();
        schedule(() => processNext.current());
      });
  };

  const enqueue = useCallback(
    (operation: XLMEditCellOperation): boolean => {
      const snapshot = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
      if (!snapshot) return false;
      const optimistic = applyOptimisticOperations(snapshot, [operation]);
      if (!optimistic) return false;
      queryClient.setQueryData(queryKey, optimistic);

      const cellKey = xlmCellAddressKey(operation.cell);
      const existing = [...items.current]
        .reverse()
        .find(
          (item) =>
            item.state !== "sending" &&
            item.operations.length === 1 &&
            item.operations[0] !== undefined &&
            xlmCellAddressKey(item.operations[0].cell) === cellKey,
        );
      if (existing) {
        const current = existing.operations[0];
        if (!current) return false;
        existing.operations[0] = mergeXLMEditCellOperations(current, operation);
      } else {
        const id = crypto.randomUUID();
        items.current.push({
          id,
          idempotencyKey: `xlm-edit-${id}`,
          operations: [operation],
          state: "queued",
          baseRevision: null,
          error: null,
          conflictRevision: null,
        });
      }
      publishState();
      schedule(() => processNext.current());
      return true;
    },
    [publishState, queryClient, queryKey],
  );

  const enqueueBatch = useCallback(
    (operations: XLMEditCellOperation[]): boolean => {
      if (operations.length === 0) return false;
      const cellKeys = operations.map((operation) =>
        xlmCellAddressKey(operation.cell),
      );
      if (new Set(cellKeys).size !== cellKeys.length) return false;
      const snapshot = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
      if (!snapshot) return false;
      const optimistic = applyOptimisticOperations(snapshot, operations);
      if (!optimistic) return false;
      queryClient.setQueryData(queryKey, optimistic);

      const id = crypto.randomUUID();
      items.current.push({
        id,
        idempotencyKey: `xlm-edit-batch-${id}`,
        operations,
        state: "queued",
        baseRevision: null,
        error: null,
        conflictRevision: null,
      });
      publishState();
      schedule(() => processNext.current());
      return true;
    },
    [publishState, queryClient, queryKey],
  );

  const retry = useCallback(() => {
    const failed = items.current.find((item) => item.state === "failed");
    if (!failed) return;
    failed.state = "queued";
    failed.error = null;
    publishState();
    schedule(() => processNext.current());
  }, [publishState]);

  const keepMine = useCallback(() => {
    const conflict = items.current.find((item) => item.state === "conflict");
    const snapshot = queryClient.getQueryData<XLMWorkbookSnapshot>(queryKey);
    if (!conflict || !snapshot) return;
    conflict.state = "queued";
    conflict.baseRevision = snapshot.xlm_snapshot.revision;
    conflict.conflictRevision = null;
    publishState();
    schedule(() => processNext.current());
  }, [publishState, queryClient, queryKey]);

  const useLatest = useCallback(async (): Promise<void> => {
    const conflict = items.current.find((item) => item.state === "conflict");
    if (!conflict) return;
    try {
      const serverSnapshot = await fetchXLMWorkbookSnapshot(
        companyId,
        versionId,
      );
      items.current = items.current.filter((item) => item.id !== conflict.id);
      queryClient.setQueryData(queryKey, serverSnapshot);
      reapplyOptimisticEdits();
      publishState();
      schedule(() => processNext.current());
    } catch (error) {
      conflict.state = "failed";
      conflict.error =
        error instanceof Error
          ? error
          : new Error("The current model revision could not be loaded.");
      publishState();
    }
  }, [
    companyId,
    publishState,
    queryClient,
    queryKey,
    reapplyOptimisticEdits,
    versionId,
  ]);

  useEffect(() => {
    mounted.current = true;
    const nextScope = `${companyId}:${versionId}`;
    if (scope.current !== nextScope) {
      scope.current = nextScope;
      items.current = [];
      processing.current = false;
      lastSavedRevision.current = null;
      setState(EMPTY_STATE);
    }
    return () => {
      mounted.current = false;
    };
  }, [companyId, versionId]);

  return { ...state, enqueue, enqueueBatch, retry, keepMine, useLatest };
}
